import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { AlertColor } from '@mui/material/Alert';
import Slide from '@mui/material/Slide';

interface NotificationContextType {
    showNotification: (message: string, severity?: AlertColor, duration?: number) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showWarning: (message: string) => void;
    showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

interface NotificationProviderProps {
    children: ReactNode;
}

interface NotificationState {
    open: boolean;
    message: string;
    severity: AlertColor;
    duration: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
        duration: 5000,
    });

    const showNotification = useCallback((message: string, severity: AlertColor = 'info', duration = 5000) => {
        setNotification({
            open: true,
            message,
            severity,
            duration,
        });
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setNotification(prev => ({ ...prev, open: false }));
    };

    const showSuccess = useCallback((message: string) => {
        showNotification(message, 'success');
    }, [showNotification]);

    const showError = useCallback((message: string) => {
        showNotification(message, 'error');
    }, [showNotification]);

    const showWarning = useCallback((message: string) => {
        showNotification(message, 'warning');
    }, [showNotification]);

    const showInfo = useCallback((message: string) => {
        showNotification(message, 'info');
    }, [showNotification]);

    const contextValue: NotificationContextType = {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };

    // Get the current notification for the Snackbar
    const currentNotification = notification.open ? notification : null;

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={currentNotification !== null}
                autoHideDuration={notification.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' } as any}
            >
                {currentNotification ? (
                    <Alert
                        onClose={handleClose}
                        severity={currentNotification.severity}
                        sx={{ width: '100%' }}
                        variant="filled"
                        elevation={6}
                    >
                        {currentNotification.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </NotificationContext.Provider>
    );
}; 